import { Easing, Tween } from '@tweenjs/tween.js'
import { tweenGroup } from './tweenGroup'
import { BufferGeometry, Color, MathUtils, Mesh, PlaneGeometry, ShaderMaterial, Vector2 } from 'three'
import { delay } from '@/utils/delay'
import { BODY_COLORS, BodyColor } from '@/const'
import App from './App'

type Options = {
	app: App
	color: BodyColor
	num: number
}

type ResizeOptions = {
	width: number
	height: number
	left: number
	top: number
	aspect: number
	line?: number
	rotate?: number
}

type ResizePillOptions = {
	index: number
	p1: Vector2
	p2: Vector2
	radius: number
}

export class Pills {
	private readonly app: App
	private readonly mesh: Mesh<BufferGeometry, ShaderMaterial>
	private readonly material: ShaderMaterial
	private readonly tweens: Tween[] = []
	private lineWidth: number = 0.001

	constructor({ app, color, num = 2 }: Options) {
		this.app = app

		this.material = new ShaderMaterial({
			transparent: true,
			depthWrite: false,
			depthTest: false,

			//language=GLSL
			vertexShader: `
				precision highp float;
				uniform float rotation;
				uniform vec2 scale;
				uniform vec2 offset;
				varying vec2 vUv;

				void main() {
					vUv = uv;
					gl_Position = vec4(position.xy * scale + offset, 1.0, 1.0);
				}
			`,
			//language=GLSL
			fragmentShader: `
				precision highp float;

				#define NUM_WORDS ${num}

				uniform float distance;
				uniform float aspect;
				uniform float lineWidth;
				uniform vec3 color;
				uniform vec2 scale;

				uniform float radius[${num}];
				uniform vec2 p1[${num}];
				uniform vec2 p2[${num}];

				varying vec2 vUv;

				float sdCircle(vec2 p, float r)
				{
					return length(p) - r;
				}

				// https://iquilezles.org/www/articles/distfunctions2d/distfunctions2d.htm
				float sdSegment(in vec2 p, in vec2 a, in vec2 b) {
					vec2 pa = p-a, ba = b-a;
					float h = clamp(dot(pa, ba)/dot(ba, ba), 0.0, 1.0);
					return length(pa - ba*h);
				}

				void main() {
					vec2 st = vUv;
					st.y *= aspect;
					st.y -= 1.0 * aspect;
					float d = 1.0;

					#if NUM_WORDS > 1
					for (int i = 0; i < NUM_WORDS; ++i){
						float p = sdSegment(st, p1[i], p2[i]) - radius[i];
						d = min(p, d);
					}
						#else
					float p = sdSegment(st, p1[0], p2[0]) - radius[0];
					d = min(p, d);
					#endif

					float afwidth = length(vec2(dFdx(d), dFdy(d))) * 0.70710678118654757;
					float c = smoothstep(distance - afwidth, distance + afwidth, d);
					float e = smoothstep(distance + lineWidth - afwidth, distance + lineWidth + afwidth, d);
					float a = c - e;
					if (a < 0.0001) discard;
					gl_FragColor = vec4(color, a);
				}
			`,
			uniforms: {
				distance: { value: -0.5 },
				lineWidth: { value: 0.25 },
				aspect: { value: 1 },
				radius: { value: new Array(num).fill(0) },
				p1: { value: new Array(num).fill(null).map(() => new Vector2()) },
				p2: { value: new Array(num).fill(null).map(() => new Vector2()) },
				color: { value: new Color(BODY_COLORS[color]) },
				scale: { value: new Vector2(1, 1) },
				offset: { value: new Vector2() },
				rotation: { value: 0 }
			}
		})

		this.mesh = new Mesh(new PlaneGeometry(2, 2, 1, 1), this.material)
		this.mesh.frustumCulled = false
		this.mesh.visible = false

		this.app.scene.add(this.mesh)
	}

	async show() {
		this.tweens.forEach((tween) => tween.stop())
		this.tweens.length = 0

		this.tweens.push(
			new Tween({ distance: -0.75, lineWidth: 0.25 }, tweenGroup)
				.to({ distance: -0.25, lineWidth: 0.25 }, 1000)
				.easing(Easing.Exponential.InOut)
				.onUpdate(({ lineWidth, distance }) => {
					this.material.uniforms.distance.value = distance
					this.material.uniforms.lineWidth.value = lineWidth
					this.app.requestRender()
				})
				.onStart(() => (this.mesh.visible = true))
				.start()
		)

		await delay(1000)

		this.tweens.push(
			new Tween({ distance: -0.25, lineWidth: 0.25 }, tweenGroup)
				.to({ distance: 0, lineWidth: this.lineWidth }, 500)
				.easing(Easing.Exponential.Out)
				.onUpdate(({ lineWidth, distance }) => {
					this.material.uniforms.distance.value = distance
					this.material.uniforms.lineWidth.value = lineWidth
					this.app.requestRender()
				})
				.start()
		)
	}

	dispose() {
		this.material.dispose()
		this.mesh.geometry.dispose()
		this.app.scene.remove(this.mesh)
		this.tweens.forEach((tween) => tween.stop())
		this.tweens.length = 0
	}

	resize({ width, height, left, top, aspect, line = 2, rotate = 0 }: ResizeOptions) {
		const w = width / this.app.sizes.width
		const h = height / this.app.sizes.height
		const x = (left + width * 0.5 - this.app.sizes.width * 0.5) / (this.app.sizes.width * 0.5)
		const y = (top + height * 0.5 - this.app.sizes.height * 0.5) / (this.app.sizes.height * 0.5)

		this.lineWidth = line / width

		this.material.uniforms.scale.value.set(w, h)
		this.material.uniforms.offset.value.set(x, y * -1)
		this.material.uniforms.aspect.value = aspect
		this.material.uniforms.lineWidth.value = this.lineWidth
		this.material.uniforms.rotation.value = MathUtils.degToRad(rotate)
	}

	resizePill({ index, p1, p2, radius }: ResizePillOptions) {
		this.material.uniforms.p1.value[index].copy(p1)
		this.material.uniforms.p2.value[index].copy(p2)
		this.material.uniforms.radius.value[index] = radius
	}

	set visible(value: boolean) {
		this.mesh.visible = value
	}

	update(y: number) {
		this.material.uniforms.offset.value.y = y * -1
	}
}
